import { create } from 'zustand';

/** Payload of the `presence.changed` realtime event. */
export interface PresenceChangedPayload {
  userId: string;
  isOnline: boolean;
}

export const PRESENCE_CHANGED = 'presence.changed';

interface PresenceState {
  /** Live presence by user id; absent = unknown (fall back to the value from the API). */
  online: Record<string, boolean>;
  setOnline: (userId: string, isOnline: boolean) => void;
  reset: () => void;
}

export const usePresenceStore = create<PresenceState>()((set) => ({
  online: {},
  setOnline: (userId, isOnline) =>
    set((state) =>
      state.online[userId] === isOnline ? state : { online: { ...state.online, [userId]: isOnline } },
    ),
  reset: () => set({ online: {} }),
}));

/**
 * Live online status of a user. Realtime `presence.changed` events win over `fallback`
 * (typically `UserRef.isOnline` from the API response).
 */
export function usePresence(userId: string | null | undefined, fallback?: boolean): boolean | undefined {
  return usePresenceStore((s) => (userId ? (s.online[userId] ?? fallback) : fallback));
}

export function isPresencePayload(value: unknown): value is PresenceChangedPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as PresenceChangedPayload).userId === 'string' &&
    typeof (value as PresenceChangedPayload).isOnline === 'boolean'
  );
}
